import { makeAutoObservable } from "mobx";
import type { RootStore } from "stores/index";
import { AccountType, ChartOfAccount } from "../types";

/**
 * User role types for GL access control
 */
export type GLUserRole = "ap_clerk" | "ar_clerk" | "accountant" | "admin";

/**
 * GL account number range patterns
 */
export const GL_ACCOUNT_RANGES = {
  ASSET: { start: 1000, end: 1999, prefix: "1" },
  LIABILITY: { start: 2000, end: 2999, prefix: "2" },
  EQUITY: { start: 3000, end: 3999, prefix: "3" },
  REVENUE: { start: 4000, end: 4999, prefix: "4" },
  EXPENSE: { start: 5000, end: 6999, prefixes: ["5", "6"] },
};

/**
 * GL accounts that require special handling
 */
export const SPECIAL_GL_ACCOUNTS = {
  ICA_CLEARING: "888",
  SUSPENSE: "999",
};

/**
 * Asset GL notification record
 */
export interface AssetGLNotification {
  id: string;
  glAccountId: string;
  glAccountNumber: string;
  glAccountName: string;
  transactionType: "invoice" | "bill" | "journal_entry" | "payment";
  transactionId: string;
  transactionNumber: string;
  amount: number;
  plantCode: string;
  createdBy: string;
  createdAt: string;
  isAcknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  notes?: string;
}

/**
 * Attachment requirement status
 */
export interface AttachmentValidation {
  isRequired: boolean;
  hasAttachment: boolean;
  isValid: boolean;
  message?: string;
  glAccountNumber?: string;
  glAccountName?: string;
}

/**
 * GL Access configuration for a role
 */
export interface GLAccessConfig {
  allowedAccountTypes: AccountType[];
  canAccessAllAccounts: boolean;
  canOverrideRestrictions: boolean;
  requiresApprovalForAssets: boolean;
}

/**
 * Role-based GL access configurations
 */
export const GL_ROLE_ACCESS: Record<GLUserRole, GLAccessConfig> = {
  ap_clerk: {
    allowedAccountTypes: [AccountType.EXPENSE],
    canAccessAllAccounts: false,
    canOverrideRestrictions: false,
    requiresApprovalForAssets: true,
  },
  ar_clerk: {
    allowedAccountTypes: [AccountType.REVENUE],
    canAccessAllAccounts: false,
    canOverrideRestrictions: false,
    requiresApprovalForAssets: true,
  },
  accountant: {
    allowedAccountTypes: [
      AccountType.ASSET,
      AccountType.LIABILITY,
      AccountType.EQUITY,
      AccountType.REVENUE,
      AccountType.EXPENSE,
    ],
    canAccessAllAccounts: true,
    canOverrideRestrictions: false,
    requiresApprovalForAssets: true,
  },
  admin: {
    allowedAccountTypes: [
      AccountType.ASSET,
      AccountType.LIABILITY,
      AccountType.EQUITY,
      AccountType.REVENUE,
      AccountType.EXPENSE,
    ],
    canAccessAllAccounts: true,
    canOverrideRestrictions: true,
    requiresApprovalForAssets: false,
  },
};

/**
 * Admin users who receive asset GL notifications
 * These are the users mentioned in the implementation plan (Brandi, Carrie)
 */
export const ASSET_GL_NOTIFICATION_RECIPIENTS = [
  { name: "Brandi Clark", email: "brandi.clark@millerprecast.com", role: "Accounting Manager" },
  { name: "Carrie", email: "carrie@millerprecast.com", role: "AR/ICA Manager" },
];

/**
 * GLControlsService
 *
 * Provides GL account access control, filtering, and validation services
 * per Implementation Plan Phase 1: GL Controls & Asset Tracking
 */
class GLControlsService {
  rootStore: RootStore;

  // Asset GL notifications
  assetNotifications: AssetGLNotification[] = [];

  // Track pending attachment requirements
  pendingAttachmentRequirements: Map<string, AttachmentValidation> = new Map();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Role Detection ====================

  /**
   * Determine the user's GL role based on their department/permissions
   */
  getUserGLRole(): GLUserRole {
    const user = this.rootStore.authStore?.user;
    if (!user) return "ap_clerk"; // Default to most restrictive

    // Check if user is admin
    if (user.adminUser) {
      return "admin";
    }

    // Check department to determine role
    const department = user.department?.toLowerCase() || "";
    const team = user.team?.toLowerCase() || "";

    if (department.includes("accounts payable") || team.includes("ap")) {
      return "ap_clerk";
    }
    if (department.includes("accounts receivable") || team.includes("ar")) {
      return "ar_clerk";
    }
    if (department.includes("accounting") || department.includes("finance")) {
      return "accountant";
    }

    // Default to accountant for unclassified users in accounting module
    return "accountant";
  }

  /**
   * Get the GL access configuration for the current user
   */
  get currentUserAccess(): GLAccessConfig {
    return GL_ROLE_ACCESS[this.getUserGLRole()];
  }

  // ==================== Account Type Detection ====================

  /**
   * Determine account type from account number
   */
  getAccountTypeFromNumber(accountNumber: string): AccountType | null {
    if (!accountNumber) return null;

    const numericPart = accountNumber.replace(/\D/g, "");
    if (!numericPart) return null;

    const firstDigit = numericPart[0];
    const accountNum = parseInt(numericPart, 10);

    if (firstDigit === "1" || (accountNum >= 1000 && accountNum <= 1999)) {
      return AccountType.ASSET;
    }
    if (firstDigit === "2" || (accountNum >= 2000 && accountNum <= 2999)) {
      return AccountType.LIABILITY;
    }
    if (firstDigit === "3" || (accountNum >= 3000 && accountNum <= 3999)) {
      return AccountType.EQUITY;
    }
    if (firstDigit === "4" || (accountNum >= 4000 && accountNum <= 4999)) {
      return AccountType.REVENUE;
    }
    if (firstDigit === "5" || firstDigit === "6" || (accountNum >= 5000 && accountNum <= 6999)) {
      return AccountType.EXPENSE;
    }

    return null;
  }

  /**
   * Check if an account number represents an asset account
   */
  isAssetAccount(accountNumber: string): boolean {
    return this.getAccountTypeFromNumber(accountNumber) === AccountType.ASSET;
  }

  /**
   * Check if an account number represents an expense account
   */
  isExpenseAccount(accountNumber: string): boolean {
    return this.getAccountTypeFromNumber(accountNumber) === AccountType.EXPENSE;
  }

  /**
   * Check if an account number represents a revenue account
   */
  isRevenueAccount(accountNumber: string): boolean {
    return this.getAccountTypeFromNumber(accountNumber) === AccountType.REVENUE;
  }

  // ==================== GL Filtering ====================

  /**
   * Filter GL accounts based on user role
   */
  filterAccountsByRole(accounts: ChartOfAccount[], userRole?: GLUserRole): ChartOfAccount[] {
    const role = userRole || this.getUserGLRole();
    const access = GL_ROLE_ACCESS[role];

    if (access.canAccessAllAccounts) {
      return accounts;
    }

    return accounts.filter((account) => {
      // Always allow special accounts (ICA Clearing, etc.)
      if (this.isSpecialAccount(account.accountNumber)) {
        return true;
      }

      return access.allowedAccountTypes.includes(account.accountType as AccountType);
    });
  }

  /**
   * Check if an account is a special account that should always be accessible
   */
  isSpecialAccount(accountNumber: string): boolean {
    const numericPart = accountNumber.replace(/\D/g, "");
    return Object.values(SPECIAL_GL_ACCOUNTS).includes(numericPart);
  }

  /**
   * Get suggested GL accounts for a vendor (default GL from vendor setup)
   */
  getSuggestedAccountsForVendor(vendorId: string, allAccounts: ChartOfAccount[]): ChartOfAccount[] {
    // TODO: Implement vendor default GL lookup when vendor service is available
    // For now, return expense accounts as default for AP
    return this.filterAccountsByRole(
      allAccounts.filter((a) => a.accountType === AccountType.EXPENSE)
    );
  }

  /**
   * Check if user can access a specific GL account
   */
  canAccessAccount(account: ChartOfAccount, userRole?: GLUserRole): boolean {
    const role = userRole || this.getUserGLRole();
    const access = GL_ROLE_ACCESS[role];

    if (access.canAccessAllAccounts) {
      return true;
    }

    if (this.isSpecialAccount(account.accountNumber)) {
      return true;
    }

    return access.allowedAccountTypes.includes(account.accountType as AccountType);
  }

  // ==================== Asset GL Alerts ====================

  /**
   * Check if a GL selection should trigger an asset alert
   */
  shouldTriggerAssetAlert(accountNumber: string): boolean {
    if (!this.isAssetAccount(accountNumber)) {
      return false;
    }

    // Admins don't need alerts
    const access = this.currentUserAccess;
    return access.requiresApprovalForAssets;
  }

  /**
   * Create an asset GL notification
   */
  createAssetNotification(params: {
    glAccountId: string;
    glAccountNumber: string;
    glAccountName: string;
    transactionType: AssetGLNotification["transactionType"];
    transactionId: string;
    transactionNumber: string;
    amount: number;
    plantCode: string;
  }): AssetGLNotification {
    const user = this.rootStore.authStore?.user;
    const notification: AssetGLNotification = {
      id: `asset-notif-${Date.now()}`,
      ...params,
      createdBy: user ? `${user.firstName} ${user.lastName}` : "Unknown",
      createdAt: new Date().toISOString(),
      isAcknowledged: false,
    };

    this.assetNotifications.push(notification);

    // In a real implementation, this would send an email/notification
    // to ASSET_GL_NOTIFICATION_RECIPIENTS

    return notification;
  }

  /**
   * Acknowledge an asset notification
   */
  acknowledgeNotification(notificationId: string, notes?: string): void {
    const notification = this.assetNotifications.find((n) => n.id === notificationId);
    if (!notification) return;

    const user = this.rootStore.authStore?.user;
    notification.isAcknowledged = true;
    notification.acknowledgedBy = user ? `${user.firstName} ${user.lastName}` : "Unknown";
    notification.acknowledgedAt = new Date().toISOString();
    if (notes) {
      notification.notes = notes;
    }
  }

  /**
   * Get unacknowledged asset notifications
   */
  get pendingAssetNotifications(): AssetGLNotification[] {
    return this.assetNotifications.filter((n) => !n.isAcknowledged);
  }

  /**
   * Get asset notifications filtered by various criteria
   */
  getFilteredAssetNotifications(filters: {
    acknowledged?: boolean;
    plantCode?: string;
    transactionType?: AssetGLNotification["transactionType"];
    dateFrom?: string;
    dateTo?: string;
  }): AssetGLNotification[] {
    return this.assetNotifications.filter((n) => {
      if (filters.acknowledged !== undefined && n.isAcknowledged !== filters.acknowledged) {
        return false;
      }
      if (filters.plantCode && n.plantCode !== filters.plantCode) {
        return false;
      }
      if (filters.transactionType && n.transactionType !== filters.transactionType) {
        return false;
      }
      if (filters.dateFrom && n.createdAt < filters.dateFrom) {
        return false;
      }
      if (filters.dateTo && n.createdAt > filters.dateTo) {
        return false;
      }
      return true;
    });
  }

  // ==================== Attachment Validation ====================

  /**
   * Validate if attachment is required and present for a GL account
   */
  validateAttachment(params: {
    glAccountNumber: string;
    glAccountName?: string;
    hasAttachment: boolean;
  }): AttachmentValidation {
    const { glAccountNumber, glAccountName, hasAttachment } = params;
    const isAsset = this.isAssetAccount(glAccountNumber);

    const validation: AttachmentValidation = {
      isRequired: isAsset,
      hasAttachment,
      isValid: !isAsset || hasAttachment,
      glAccountNumber,
      glAccountName,
    };

    if (isAsset && !hasAttachment) {
      validation.message = `Attachment required for asset account ${glAccountNumber}${
        glAccountName ? ` (${glAccountName})` : ""
      }. Please attach supporting documentation.`;
    }

    return validation;
  }

  /**
   * Validate multiple line items for attachment requirements
   */
  validateLineItemsAttachment(
    lineItems: Array<{ glAccountNumber: string; glAccountName?: string }>,
    hasAttachment: boolean
  ): AttachmentValidation {
    // Check if any line item uses an asset account
    const assetLineItems = lineItems.filter((item) => this.isAssetAccount(item.glAccountNumber));

    if (assetLineItems.length === 0) {
      return {
        isRequired: false,
        hasAttachment,
        isValid: true,
      };
    }

    const firstAssetItem = assetLineItems[0];
    return {
      isRequired: true,
      hasAttachment,
      isValid: hasAttachment,
      glAccountNumber: firstAssetItem.glAccountNumber,
      glAccountName: firstAssetItem.glAccountName,
      message: hasAttachment
        ? undefined
        : `Attachment required for asset account transactions. ${assetLineItems.length} line item(s) use asset accounts.`,
    };
  }

  /**
   * Check if a transaction can be submitted based on attachment requirements
   */
  canSubmitTransaction(params: {
    lineItems: Array<{ glAccountNumber: string; glAccountName?: string }>;
    hasAttachment: boolean;
    userRole?: GLUserRole;
  }): { canSubmit: boolean; reason?: string } {
    const { lineItems, hasAttachment, userRole } = params;
    const role = userRole || this.getUserGLRole();
    const access = GL_ROLE_ACCESS[role];

    // Admins can override attachment requirements
    if (access.canOverrideRestrictions) {
      return { canSubmit: true };
    }

    const validation = this.validateLineItemsAttachment(lineItems, hasAttachment);

    if (!validation.isValid) {
      return {
        canSubmit: false,
        reason: validation.message,
      };
    }

    return { canSubmit: true };
  }

  // ==================== Utility Methods ====================

  /**
   * Get display label for account type
   */
  getAccountTypeLabel(accountType: AccountType): string {
    const labels: Record<AccountType, string> = {
      [AccountType.ASSET]: "Asset",
      [AccountType.LIABILITY]: "Liability",
      [AccountType.EQUITY]: "Equity",
      [AccountType.REVENUE]: "Revenue",
      [AccountType.EXPENSE]: "Expense",
    };
    return labels[accountType];
  }

  /**
   * Get color for account type (for UI display)
   */
  getAccountTypeColor(accountType: AccountType): "success" | "error" | "primary" | "info" | "warning" {
    const colors: Record<AccountType, "success" | "error" | "primary" | "info" | "warning"> = {
      [AccountType.ASSET]: "success",
      [AccountType.LIABILITY]: "error",
      [AccountType.EQUITY]: "primary",
      [AccountType.REVENUE]: "info",
      [AccountType.EXPENSE]: "warning",
    };
    return colors[accountType];
  }

  /**
   * Format account for dropdown display
   */
  formatAccountForDisplay(account: ChartOfAccount): string {
    return `${account.accountNumber} - ${account.accountName}`;
  }

  /**
   * Get account number prefix guidelines
   */
  getAccountNumberGuidelines(): string {
    return `
Account Number Guidelines:
• 1xxx - Asset accounts
• 2xxx - Liability accounts
• 3xxx - Equity accounts
• 4xxx - Revenue accounts
• 5xxx-6xxx - Expense accounts
• 888 - ICA Clearing (special)
• 999 - Suspense (special)
    `.trim();
  }
}

export default GLControlsService;
